import { useLocation, useMatch, useNavigate } from '@tanstack/react-router';
import { Download, ExternalLink, Folder, MessageCircle } from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useOpenInCasePartner } from '@/components/case-partner/hooks/useOpenInCasePartner';
import { ScrollableFolderPath } from '@/components/folders/ScrollableFolderPath';
import type { Highlight, HighlightGroup } from '@/components/pdf/types/highlight-types';
import { useCitationTarget } from '@/hooks/useCitationTarget';
import { backendClient } from '@/lib/api-client';
import { downloadFile } from '@/lib/utils';
import { getFileExtension, isImageFileName, isPowerPointFileName, isSpreadsheetFileName, isTextFileName, isWordFileName } from '@/utils/file-utils';
import Loading from '../shared/Loading';
import { OfficePreviewUnavailable } from '../shared/OfficePreviewUnavailable';

const PDFViewer = lazy(() => import('@/components/pdf/PDFViewer'));
const EmailViewer = lazy(() => import('@/pages/matter/[slug]/email-viewer'));
const ExcelViewer = lazy(() => import('@/components/excel/ExcelViewer'));
const ImageViewer = lazy(() => import('@/components/image/ImageViewer'));
const WordDocViewer = lazy(() => import('@/pages/matter/[slug]/WordDocViewer'));
const TextFileViewer = lazy(() => import('@/components/text/TextFileViewer'));

const ViewerFallback = () => (
  <div className="flex items-center justify-center h-full">
    <Loading />
  </div>
);

type Segment = { canonical_id?: string | number; label?: string; content: string; locator: any };
interface Citations {
  entities: Segment[];
  events: Segment[];
  deadlines: Segment[];
}

const EMPTY_CITATIONS: Citations = { entities: [], events: [], deadlines: [] };

const CITATION_COLORS: Record<string, string> = {
  entities: '#6b8cae',
  deadlines: '#c47070',
  events: '#4d8c6f',
};

export type CitationFocus = { type: 'entities' | 'events' | 'deadlines'; canonicalId: string };

interface DocumentViewerContentProps {
  documentGuid: string;
  matterGuid?: string;
  chunkId?: number;
  segmentIndex?: number;
  cellRange?: string;
  initialPageNumber?: number;
  attachmentGuid?: string;
  focusCitation?: CitationFocus;
}

export const DocumentViewerContent: React.FC<DocumentViewerContentProps> = ({
  documentGuid,
  matterGuid,
  chunkId,
  segmentIndex,
  cellRange,
  initialPageNumber,
  attachmentGuid,
  focusCitation,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const openInCasePartner = useOpenInCasePartner();
  const routeSlug = useMatch({
    from: '/matter/$slug',
    shouldThrow: false,
    select: (match) => match.params.slug,
  });
  const slug = matterGuid || routeSlug;
  const [fileUrl, setFileUrl] = useState<string>('');
  const [fileData, setFileData] = useState<{
    fileName: string;
    displayName: string;
    fileType: string;
    contentType: string;
    convertedToPdf?: boolean;
    folderPath?: string;
    folderGuid?: string;
    documentGuid?: string;
    fileReferenceGuid?: string;
    isDraftingManaged?: boolean;
    draftDocumentGuid?: string | null;
  } | null>(null);
  const [resolvedAttachmentGuid, setResolvedAttachmentGuid] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [citations, setCitations] = useState<Citations>(EMPTY_CITATIONS);
  const citationTarget = useCitationTarget({
    slug,
    documentGuid,
    chunkId,
    segmentIndex,
    cellRange,
    initialPageNumber,
  });

  // Fetch document citations.
  const citationsGuid = resolvedAttachmentGuid || fileData?.fileReferenceGuid;
  useEffect(() => {
    if (!focusCitation) {
      setCitations(EMPTY_CITATIONS);
      return;
    }
    if (!slug || !citationsGuid) {
      setCitations(EMPTY_CITATIONS);
      return;
    }

    let cancelled = false;
    setCitations(EMPTY_CITATIONS);

    const fetchCitations = async () => {
      try {
        const response = await backendClient.get(
          `/matters/${slug}/file-references/${citationsGuid}/citations`,
          focusCitation.type === 'events' ? { params: { type: 'events' } } : undefined
        );
        if (cancelled) return;
        setCitations({
          entities: response.data?.entities ?? [],
          events: response.data?.events ?? [],
          deadlines: response.data?.deadlines ?? [],
        });
      } catch (error) {
        console.error('Error fetching citations:', error);
        if (!cancelled) setCitations(EMPTY_CITATIONS);
      }
    };

    fetchCitations();
    return () => {
      cancelled = true;
    };
  }, [slug, citationsGuid, focusCitation?.type, focusCitation?.canonicalId]);



  // Build highlights: only highlight what the caller asked for via focusCitation.
  // No focusCitation = no highlights.
  const focusHighlightGroups: HighlightGroup[] = useMemo(() => {
    if (!focusCitation) return [];

    // Group key: `${page}:${bbox}` for segment-located citations, or
    // `text:${searchText}` for legacy searchText-only citations. Different
    // namespaces so a coincidentally-empty bbox can't collide with a text key.
    const segKey = (seg: Segment): string | null => {
      if (seg.locator?.page && seg.locator?.bbox) {
        return `bbox:${seg.locator.page}:${seg.locator.bbox.join(',')}`;
      }
      if (seg.locator?.searchText) {
        return `text:${seg.locator.searchText}`;
      }
      return null;
    };
    const locatedSegments = (seg: Segment): Segment[] => {
      const regions: unknown[] = Array.isArray(seg.locator?.regions) ? seg.locator.regions : [];
      const located = regions.flatMap((region) => {
        if (!region || typeof region !== 'object') return [];
        const candidate = region as { page?: unknown; bbox?: unknown };
        return typeof candidate.page === 'number' && Array.isArray(candidate.bbox)
          ? [{ ...seg, locator: { ...seg.locator, page: candidate.page, bbox: candidate.bbox } }]
          : [];
      });
      return located.length > 0 ? located : [seg];
    };
    const grouped = new Map<string, { seg: Segment; entries: { label: string; color: string }[] }>();
    const color = CITATION_COLORS[focusCitation.type] ?? '#6b8cae';

    // Legacy (origin/main) deadline mode: the doc was ingested before segments,
    // so all deadline locators carry searchText only. Match origin/main's
    // task-highlight rendering — show every deadline-task match in the doc
    // (yellow, 0.7 opacity), with the clicked one as key for scroll.
    const isLegacyDeadlines = focusCitation.type === 'deadlines'
      && Array.isArray(citations.deadlines)
      && citations.deadlines.some(
        s => s.locator?.searchText && !s.locator?.bbox && !s.locator?.regions,
      );

    // Entity / event / deadline focus — highlight matching canonical.
    // Legacy deadline mode is an exception: include every deadline in the doc.
    const typeSegs = citations[focusCitation.type];
    for (const sourceSegment of typeSegs) {
      if (!sourceSegment.label) continue;
      const label = sourceSegment.label;
      if (
        !isLegacyDeadlines
        && focusCitation.canonicalId
        && String(sourceSegment.canonical_id ?? sourceSegment.label)
          !== String(focusCitation.canonicalId)
      ) continue;
      for (const seg of locatedSegments(sourceSegment)) {
        const key = segKey(seg);
        if (!key) continue;
        const existing = grouped.get(key);
        if (existing) {
          if (!existing.entries.some(e => e.label === label)) {
            existing.entries.push({ label, color });
          }
        } else {
          grouped.set(key, { seg, entries: [{ label, color }] });
        }
      }
    }

    if (grouped.size === 0) return [];

    const focusedCanonical = focusCitation.canonicalId ? String(focusCitation.canonicalId) : null;
    // origin/main task-highlight palette (see buildHighlightGroups in highlight-utils.ts)
    const LEGACY_COLOR = '#ffeb3b';
    const LEGACY_OPACITY = 0.7;

    const highlights: Highlight[] = Array.from(grouped.values()).map(({ seg, entries }, i) => {
      const hasBbox = !!(seg.locator?.page && seg.locator?.bbox);
      const isFocused = focusedCanonical !== null
        && String(seg.canonical_id ?? '') === focusedCanonical;
      const base: Highlight = {
        id: `cite-${i}`,
        color: isLegacyDeadlines ? LEGACY_COLOR : (entries[0]?.color ?? color),
        opacity: isLegacyDeadlines ? LEGACY_OPACITY : 0.25,
        // For bbox highlights, PDFViewer derives the scroll target from the bbox itself.
        // For searchText-only highlights, the hook sets targetPageIndex only when
        // isKeyHighlight is true — flag the focused canonical, or the first match
        // when no specific canonical was requested.
        isKeyHighlight: !hasBbox && (isLegacyDeadlines ? isFocused : i === 0),
        // Legacy mode also needs taskId in metadata so PDFViewer's deadline
        // hover menu (View/Edit/Delete) appears. canonical_id is the evidence
        // Event.guid; the overlay resolves it against synthetic id / event_guid
        // / base_event_guids (they are no longer the same value).
        metadata: isLegacyDeadlines
          ? { labels: entries, taskId: seg.canonical_id }
          : { labels: entries },
      };
      if (hasBbox) {
        return { ...base, bbox: { page: seg.locator.page, polygon: seg.locator.bbox } };
      }
      return { ...base, searchText: seg.locator.searchText };
    });

    return [{
      // 'deadline' group type triggers PDFViewer's hover menu; 'citation' suppresses it.
      type: isLegacyDeadlines ? 'deadline' : 'citation',
      label: isLegacyDeadlines ? 'Deadlines' : 'Citations',
      visible: true,
      highlights,
    }];
  }, [citations, focusCitation]);

  const highlightGroups = citationTarget.highlightGroups.length > 0
    ? citationTarget.highlightGroups
    : focusHighlightGroups;

  const focusExcelLocator = useMemo(() => {
    if (!focusCitation) return null;
    const colorSegs = citations[focusCitation.type] ?? [];
    const match = colorSegs.find((seg) => {
      if (seg.locator?.type !== 'excel') return false;
      if (!focusCitation.canonicalId) return true;
      return String(seg.canonical_id ?? seg.label) === String(focusCitation.canonicalId);
    });
    return match?.locator ?? null;
  }, [citations, focusCitation]);

  const focusTextLocator = useMemo(() => {
    if (!focusCitation) return null;
    const colorSegs = citations[focusCitation.type] ?? [];
    const match = colorSegs.find((seg) => {
      if (seg.locator?.type !== 'text') return false;
      if (!focusCitation.canonicalId) return true;
      return String(seg.canonical_id ?? seg.label) === String(focusCitation.canonicalId);
    });
    const start = match?.locator?.char_start;
    const end = match?.locator?.char_end;
    if (typeof start !== 'number' || typeof end !== 'number') return null;
    return { start, end };
  }, [citations, focusCitation]);

  // Determine file type from file name or extension
  const getFileType = (fileName: string): 'pdf' | 'msg' | 'word' | 'powerpoint' | 'excel' | 'image' | 'text' | null => {
    if (!fileName) return null;
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'msg' || extension === 'eml') return 'msg';
    if (isWordFileName(fileName)) return 'word';
    if (isPowerPointFileName(fileName)) return 'powerpoint';
    if (isSpreadsheetFileName(fileName)) return 'excel';
    if (isImageFileName(fileName)) return 'image';
    if (isTextFileName(fileName)) return 'text';
    return 'pdf';
  };

  // Fetch the file content from the backend
  useEffect(() => {
    if (!slug) return;

    if (!documentGuid) {
      setFileUrl('');
      setFileData(null);
      setResolvedAttachmentGuid(null);
      setIsLoading(false);
      return;
    }

    const fetchFileContent = async () => {
      try {
        setIsLoading(true);
        const response = await backendClient.get(
          `/correspondence/${slug}/get-file`,
          { params: { file_reference_guid: documentGuid } }
        );

        const data = response.data;

        if (data.parentEmail) {
          // Attachment: store the attachment's own FileRef GUID, then use parent email as fileData
          setResolvedAttachmentGuid(data.fileReferenceGuid || documentGuid);
          setFileUrl('');
          setFileData({
            fileName: data.parentEmail.fileName,
            displayName: data.parentEmail.displayName,
            fileType: '.msg',
            contentType: 'message/rfc822',
            folderPath: data.parentEmail.folderPath,
            folderGuid: data.parentEmail.folderGuid,
            documentGuid: data.parentEmail.documentGuid,
            fileReferenceGuid: data.parentEmail.fileReferenceGuid,
          });
        } else {
          if (!data.signedUrl) throw new Error('Invalid response format from API');
          setResolvedAttachmentGuid(null);
          setFileUrl(data.signedUrl);
          setFileData(data);
        }
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setFileData({
            fileName: 'email.msg',
            displayName: 'Email',
            fileType: 'msg',
            contentType: 'message/rfc822',
          });
        } else {
          console.error('Error fetching file content:', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchFileContent();
  }, [documentGuid, slug]);

  const handleOpenInNewTab = () => {
    if (slug && documentGuid) {
      if (fileData?.isDraftingManaged && fileData.draftDocumentGuid) {
        window.open(`/matter/${slug}/drafting/${fileData.draftDocumentGuid}`, '_blank', 'noopener,noreferrer');
        return;
      }
      const targetGuid = fileData?.fileReferenceGuid || documentGuid;
      const url = `/matter/${slug}/f/${targetGuid}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenInCasePartner = () => {
    const actualDocGuid = fileData?.documentGuid || documentGuid;
    openInCasePartner(slug, {
      guid: actualDocGuid,
      displayName: fileData?.displayName,
      fileName: fileData?.fileName,
    });
  };

  const handleFolderClick = () => {
    const folderGuid = fileData?.folderGuid;
    if (slug && folderGuid) {
      navigate({
        to: '/matter/$slug/case-files/folder/$folderGuid',
        params: { slug, folderGuid },
      });
    }
  };

  const handleDownload = async () => {
    if (!slug || !documentGuid) return;
    try {
      // For attachments use the parent email's fileReferenceGuid
      const targetGuid = fileData?.fileReferenceGuid || documentGuid;
      const response = await backendClient.get(
        `/correspondence/${slug}/get-file?file_reference_guid=${targetGuid}&download=true`
      );
      if (response.data.signedUrl && response.data.fileName) {
        await downloadFile(response.data.signedUrl, response.data.fileName);
      }
    } catch (error) {
      console.error('Error downloading original file:', error);
      toast.error('Could not download the file.');
    }
  };

  const isEmailViewer = fileData ? getFileType(fileData.fileName) === 'msg' || !!resolvedAttachmentGuid : false;
  const isExcelViewer = fileData ? getFileType(fileData.fileName) === 'excel' : false;
  const isImageViewer = fileData ? getFileType(fileData.fileName) === 'image' : false;
  const isLegacyWordFallback = fileData
    ? getFileType(fileData.fileName) === 'word'
      && !fileData.convertedToPdf
      && getFileExtension(fileData.fileName).toLowerCase() === '.doc'
    : false;
  const isWordFallback = fileData
    ? getFileType(fileData.fileName) === 'word' && !fileData.convertedToPdf && !isLegacyWordFallback
    : false;
  const isPowerPointFallback = fileData
    ? getFileType(fileData.fileName) === 'powerpoint' && !fileData.convertedToPdf
    : false;
  const isTextViewer = fileData
    ? getFileType(fileData.fileName) === 'text'
    : false;
  const showLoadingOverlay = isLoading;

  return (
    <div className="relative h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-slate-200 shrink-0">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">
              {fileData?.displayName || 'Document'}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!location.pathname.includes('case-partner') && (
              <button
                onClick={handleOpenInCasePartner}
                className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-slate-900 transition-all px-1.5 py-0.5 rounded-sm hover:bg-slate-100"
                title="Open in Case Partner"
              >
                <MessageCircle className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-slate-900 transition-all px-1.5 py-0.5 rounded-sm hover:bg-slate-100"
              title="Download"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={handleOpenInNewTab}
              className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-slate-900 transition-all px-1.5 py-0.5 rounded-sm hover:bg-slate-100"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </button>
          </div>
        </div>
        {fileData?.folderPath && (
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-slate-500">
            <Folder className="h-3 w-3 shrink-0" />
            <ScrollableFolderPath
              className="flex-1"
              contentClassName="min-w-max"
            >
              {fileData.folderGuid ? (
                <button
                  type="button"
                  onClick={handleFolderClick}
                  className="shrink-0 cursor-pointer text-left hover:underline"
                  title={fileData.folderPath}
                  aria-label={`Open folder ${fileData.folderPath}`}
                >
                  {fileData.folderPath}
                </button>
              ) : (
                <span className="shrink-0" title={fileData.folderPath}>
                  {fileData.folderPath}
                </span>
              )}
            </ScrollableFolderPath>
          </div>
        )}

      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden bg-slate-50 relative">
        {!fileData && !isLoading ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-slate-500">Failed to load document</p>
          </div>
        ) : fileData && slug && isEmailViewer ? (
          <Suspense fallback={<ViewerFallback />}>
            <EmailViewer
              documentGuid={fileData.fileReferenceGuid || documentGuid}
              matterGuid={slug}
              showAttachmentTabs={true}
              initialAttachmentGuid={resolvedAttachmentGuid || attachmentGuid}
              attachmentHighlightGroups={highlightGroups}
              attachmentExcelLocator={citationTarget.excelLocator || focusExcelLocator}
              attachmentTextLocator={citationTarget.textLocator}
              attachmentTextScrollOffset={citationTarget.chunkStartOffset}
            />
          </Suspense>
        ) : fileData && slug && isExcelViewer ? (
          <Suspense fallback={<ViewerFallback />}>
            <ExcelViewer
              matterGuid={slug}
              fileReferenceGuid={fileData.fileReferenceGuid || documentGuid}
              documentGuid={fileData.documentGuid || documentGuid}
              fileName={fileData.fileName}
              citationLocator={citationTarget.excelLocator || focusExcelLocator}
            />
          </Suspense>
        ) : fileData && isImageViewer ? (
          <Suspense fallback={<ViewerFallback />}>
            <ImageViewer
              fileUrl={fileUrl}
              fileName={fileData.fileName}
            />
          </Suspense>
        ) : fileData && isWordFallback ? (
          <Suspense fallback={<ViewerFallback />}>
            <WordDocViewer fileUrl={fileUrl} />
          </Suspense>
        ) : fileData && (isLegacyWordFallback || isPowerPointFallback) ? (
          <OfficePreviewUnavailable onDownload={handleDownload} />
        ) : fileData && isTextViewer ? (
          <Suspense fallback={<ViewerFallback />}>
            <TextFileViewer
              fileUrl={fileUrl}
              fileName={fileData.fileName}
              citationRange={citationTarget.textLocator ?? focusTextLocator}
              scrollOffset={citationTarget.chunkStartOffset}
            />
          </Suspense>
        ) : fileData && slug ? (
          <Suspense fallback={<ViewerFallback />}>
            <PDFViewer
              key={fileUrl}
              fileUrl={fileUrl}
              highlightGroups={highlightGroups}
              slug={slug}
              fileName={fileData.fileName}
              sourceDocumentGuid={fileData.documentGuid || documentGuid}
              initialPage={citationTarget.pageNumber}
              charOffset={citationTarget.chunkStartOffset ?? undefined}
            />
          </Suspense>
        ) : null}
      </div>
      {showLoadingOverlay && (
        <div className="absolute inset-0 bg-white z-50 flex items-center justify-center">
          <Loading text="Loading document..." />
        </div>
      )}
    </div>
  );
};
